import { LitElement, html } from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';
import AabInputblock from '@aab/sc-aab-inputblock/src/AabInputblock';

export default class AabInputblockNumeric extends ScopedElementsMixin(LitElement) {
  static get properties() {
    return {
      inputId: { type: String, attribute: 'input-id', reflect: true },
      required: { type: Boolean },
      minLength: { type: Number, attribute: 'minlength', reflect: true },
      maxLength: { type: Number, attribute: 'maxlength', reflect: true },
      minValue: { type: Number, attribute: 'min-value', reflect: true },
      maxValue: { type: Number, attribute: 'max-value', reflect: true },
      placeholder: { type: String },
      value: { type: String },
      disablePaste: { type: Boolean, attribute: 'disable-paste', reflect: true },
      inputWidth: { type: String, attribute: 'input-width', reflect: true },
      text: { type: Object },
      messageType: { type: String, attribute: 'message-type', reflect: true },
      disabled: { type: Boolean },
    };
  }

  constructor() {
    super();
    this.required = false;
    this.value = '';
    this.text = {
      label: '',
      error_tooShort: 'Your input is too short',
      error_required: 'This field is required',
      error_tooLow: 'Your input is too low. Enter a greater value',
      error_tooHigh: 'Your input is too high. Enter a lesser value',
    };
    this.errorMessage = '';
    this.inputId = '';
    this.validateOnInput = false;
    this.validateOnBlur = true;
    this.minLength = 1;
    this.maxLength = 20;
    this.disablePaste = false;
    this.inputWidth = '';
    this.placeholder = '';
    this.messageType = 'warning';
    this.disabled = false;
  }

  static get scopedElements() {
    return {
      'aab-inputblock': AabInputblock,
    };
  }

  render() {
    return html`
      <aab-inputblock
        input-type="text"
        input-id=${this.inputId}
        ?required=${this.required}
        maxlength=${this.maxLength}
        placeholder=${this.placeholder}
        label=${this.text.label}
        .value=${this.value}
        message=${this.errorMessage}
        message-type=${this.messageType}
        ?disable-paste=${this.disablePaste}
        ?disabled=${this.disabled}
        input-width=${this.inputWidth}
        @aab-inputblock-change=${(event) => this.handleChangeEvent(event)}
        @aab-inputblock-input=${(event) => this.handleInputEvent(event)}
        @aab-inputblock-invalid=${(event) => this.handleInvalidEvent(event)}
        @aab-inputblock-blur=${(event) => this.handleBlurEvent(event)}
      ></aab-inputblock>
    `;
  }

  handleChangeEvent(e) {
    this.validateOnInput = true;
    this.value = e.detail.value.replace(/[^0-9]/g, '');
    e.detail.srcElement.value = this.value;
    this.validateAndDispatch(e.detail.srcElement);
  }

  handleInvalidEvent(e) {
    this.validateOnInput = true;
    this.validateAndDispatch(e.detail.srcElement);
  }

  handleInputEvent(e) {
    const cleanedValue = e.detail.value.replace(/[^0-9]/g, '');
    if (e.detail.value !== cleanedValue) {
      e.detail.srcElement.value = cleanedValue;
    }
    if (this.value !== cleanedValue) {
      this.value = cleanedValue;
      e.detail.srcElement.value = this.value;
      this.validateOnBlur = false;
      if (this.validateOnInput || (this.value.length >= this.minLength)) {
        this.validateOnInput = true;
        this.validateAndDispatch(e.detail.srcElement);
      } else {
        this.dispatchNumericEvent(this.value);
      }
    }
  }

  handleBlurEvent(e) {
    if (this.validateOnBlur) {
      this.validateOnInput = true;
      this.validateAndDispatch(e.detail.srcElement);
    }
  }

  dispatchNumericEvent(value) {
    this.dispatchEvent(
      new CustomEvent(
        'aab-inputblock-numeric-updated', {
          detail: { value },
          bubbles: true,
          composed: true,
        },
      ),
    );
  }

  validateAndDispatch(inputElement) {
    if (this.required && this.value.length === 0) {
      this.errorMessage = this.text.error_required;
      inputElement.setCustomValidity('required');
    } else if (this.value.length > 0 && this.value.length < this.minLength) {
      this.errorMessage = this.text.error_tooShort;
      inputElement.setCustomValidity('tooshort');
    } else if (this.value.length > 0 && ((this.minValue && this.value < this.minValue))) {
      this.errorMessage = this.text.error_tooLow;
      inputElement.setCustomValidity('tooLow');
    } else if (this.value.length > 0 && ((this.maxValue && this.value > this.maxValue))) {
      this.errorMessage = this.text.error_tooHigh;
      inputElement.setCustomValidity('tooHigh');
    } else {
      this.errorMessage = '';
      inputElement.setCustomValidity('');
    }
    this.requestUpdate();
    this.dispatchNumericEvent(this.value);
  }
}
